import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, Ticket, Mail, Download, RefreshCw, Search, CheckCircle2, Circle } from "lucide-react";
import { apiGet, apiPatch, clearToken, getToken } from "../lib/auth";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-white/10 bg-white/[0.02] p-6 transition-colors duration-300 hover:border-[#FF0000]/60"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {label}
        </span>
        <Icon className="h-4 w-4 text-[#FF0000]" />
      </div>
      <div className="mt-4 font-display text-5xl leading-none tabular-nums text-white">
        {value ?? "—"}
      </div>
    </div>
  );
}

function formatDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return d;
  }
}

function toCsv(rows) {
  if (!rows?.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
}

function download(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [interest, setInterest] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [unusedOnly, setUnusedOnly] = useState(false);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const cutoffs = {
      today: now - 24 * 60 * 60 * 1000,
      "7d": now - 7 * 24 * 60 * 60 * 1000,
      "30d": now - 30 * 24 * 60 * 60 * 1000,
    };
    return leads.filter((l) => {
      if (unusedOnly && l.used) return false;
      if (dateFilter !== "all") {
        const t = new Date(l.created_at).getTime();
        if (t < cutoffs[dateFilter]) return false;
      }
      if (!q) return true;
      const hay = `${l.name} ${l.email} ${l.message || ""} ${l.promo_code}`.toLowerCase();
      return hay.includes(q);
    });
  }, [leads, search, dateFilter, unusedOnly]);

  const toggleUsed = async (lead) => {
    const nextUsed = !lead.used;
    // optimistic update
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, used: nextUsed } : l)));
    try {
      await apiPatch(`/admin/leads/${lead.id}`, { used: nextUsed });
      toast.success(nextUsed ? "Code marqué comme utilisé" : "Code remis à disponible");
    } catch {
      // revert
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, used: lead.used } : l)));
      toast.error("Impossible de mettre à jour");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, statsRes, leadsRes, intRes] = await Promise.all([
        apiGet("/auth/me"),
        apiGet("/admin/stats"),
        apiGet("/admin/leads"),
        apiGet("/admin/ticket-interest"),
      ]);
      setMe(meRes);
      setStats(statsRes);
      setLeads(leadsRes);
      setInterest(intRes);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []);

  if (!getToken()) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    clearToken();
    toast.success("Déconnecté");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 lg:px-10">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl leading-none tracking-tight text-[#FF0000]">
              AKTRA
            </span>
            <span className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              · admin · Éclipse
            </span>
          </div>
          <div className="flex items-center gap-4">
            {me && (
              <span className="hidden font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-400 sm:inline">
                {me.email}
              </span>
            )}
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-2 font-mono-x text-[10px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-[#FF0000] hover:text-[#FF0000]"
              title="Rafraîchir"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 bg-[#FF0000] px-3 py-2 font-mono-x text-[10px] uppercase tracking-[0.25em] text-white hover:bg-[#ff2424]"
            >
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 py-10 lg:px-10 lg:py-14">
        {/* title */}
        <div className="mb-8 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <p className="mb-2 font-mono-x text-[10px] uppercase tracking-[0.3em] text-[#FF0000]">
              [ Tableau de bord ]
            </p>
            <h1 className="font-display text-5xl uppercase leading-[0.9] tracking-tight lg:text-6xl">
              Éclipse — data
            </h1>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-zinc-400">
            Vue temps réel des inscriptions LemFi, des intentions d’achat et de l’activité de la
            billetterie Éclipse.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-px sm:grid-cols-3">
          <StatCard
            label="Leads LemFi (total)"
            value={stats?.lemfi_leads_total}
            icon={Users}
            testid="stat-leads-total"
          />
          <StatCard
            label="Leads LemFi (aujourd’hui)"
            value={stats?.lemfi_leads_today}
            icon={Mail}
            testid="stat-leads-today"
          />
          <StatCard
            label="Intérêts billet"
            value={stats?.ticket_interest_total}
            icon={Ticket}
            testid="stat-interest-total"
          />
        </div>

        {/* Leads table */}
        <section className="mt-14">
          <div className="mb-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <h2 className="font-display text-2xl uppercase tracking-tight text-white">
              Inscriptions LemFi ({filteredLeads.length}/{leads.length})
            </h2>
            <button
              onClick={() => download(`aktra-lemfi-leads-${Date.now()}.csv`, toCsv(filteredLeads))}
              disabled={!filteredLeads.length}
              data-testid="export-leads"
              className="flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-2 font-mono-x text-[10px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-[#FF0000] hover:text-[#FF0000] disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>

          {/* Filter bar */}
          <div className="mb-4 flex flex-col gap-3 border border-white/10 bg-white/[0.02] p-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Chercher par nom, email, message, code..."
                className="w-full border border-white/10 bg-white/5 py-2 pl-9 pr-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#FF0000]"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {[
                { key: "all", label: "Tout" },
                { key: "today", label: "24 h" },
                { key: "7d", label: "7 j" },
                { key: "30d", label: "30 j" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setDateFilter(opt.key)}
                  data-testid={`filter-${opt.key}`}
                  className={`border px-3 py-2 font-mono-x text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                    dateFilter === opt.key
                      ? "border-[#FF0000] bg-[#FF0000]/10 text-white"
                      : "border-white/10 bg-white/5 text-zinc-400 hover:border-white/20 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <label
              data-testid="filter-unused"
              className="flex cursor-pointer items-center gap-2 border border-white/10 bg-white/5 px-3 py-2 font-mono-x text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white"
            >
              <input
                type="checkbox"
                checked={unusedOnly}
                onChange={(e) => setUnusedOnly(e.target.checked)}
                className="accent-[#FF0000]"
              />
              Non utilisés
            </label>
          </div>

          <div className="overflow-hidden border border-white/10" data-testid="leads-table">
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0f0f11] font-mono-x text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <tr>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Nom</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Message</th>
                    <th className="p-3 text-left">Code</th>
                    <th className="p-3 text-left">Utilisé</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-zinc-500">
                        Aucun résultat.
                      </td>
                    </tr>
                  )}
                  {filteredLeads.map((l) => (
                    <tr
                      key={l.id}
                      className={`border-t border-white/5 transition-colors hover:bg-white/[0.03] ${l.used ? "opacity-60" : ""}`}
                    >
                      <td className="p-3 font-mono-x text-xs text-zinc-400">
                        {formatDate(l.created_at)}
                      </td>
                      <td className="p-3 text-white">{l.name}</td>
                      <td className="p-3 text-zinc-300">{l.email}</td>
                      <td className="p-3 max-w-md truncate text-zinc-400" title={l.message}>
                        {l.message || "—"}
                      </td>
                      <td className={`p-3 font-mono-x ${l.used ? "text-zinc-500 line-through" : "text-[#FF0000]"}`}>
                        {l.promo_code}
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => toggleUsed(l)}
                          data-testid={`toggle-used-${l.id}`}
                          className="flex items-center gap-2 font-mono-x text-[10px] uppercase tracking-[0.15em] text-zinc-400 hover:text-white"
                          title={l.used ? "Marquer comme non utilisé" : "Marquer comme utilisé"}
                        >
                          {l.used ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-[#22C55E]" />
                              <span className="text-[#22C55E]">Oui</span>
                            </>
                          ) : (
                            <>
                              <Circle className="h-4 w-4" />
                              <span>Non</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Ticket interest table */}
        <section className="mt-14">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl uppercase tracking-tight text-white">
              Intérêts billet ({interest.length})
            </h2>
            <button
              onClick={() => download(`aktra-ticket-interest-${Date.now()}.csv`, toCsv(interest))}
              disabled={!interest.length}
              data-testid="export-interest"
              className="flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-2 font-mono-x text-[10px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-[#FF0000] hover:text-[#FF0000] disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
          <div className="overflow-hidden border border-white/10">
            <div className="max-h-[400px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0f0f11] font-mono-x text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <tr>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {interest.length === 0 && (
                    <tr>
                      <td colSpan={3} className="p-6 text-center text-zinc-500">
                        Aucun intérêt enregistré.
                      </td>
                    </tr>
                  )}
                  {interest.map((it) => (
                    <tr key={it.id} className="border-t border-white/5">
                      <td className="p-3 font-mono-x text-xs text-zinc-400">
                        {formatDate(it.created_at)}
                      </td>
                      <td className="p-3 text-white">{it.email}</td>
                      <td className="p-3 text-zinc-400">{it.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
